import React from 'react';
import styled, {css} from 'styled-components';
import {colors} from 'styles/colors';
import {useFormatMsg} from 'utils/useFormatMsg';
import RadioButton from './RadioButton';
import DefaultText from './DefaultText';
import {minmax} from 'utils/minmax';
import DefaultImageView from './DefaultImageView';

/**
 * 라벨 라디오 컴포넌트의 Props 인터페이스
 * @property {string} [labelText] - 라벨 텍스트
 * @property {any} value - 선택된 값
 * @property {any} [setValue] - 값 설정 함수
 * @property {boolean} [isColumn] - 세로 방향 여부
 * @property {string} [errorText] - 에러 텍스트
 * @property {React.CSSProperties} [errorTextStyle] - 에러 텍스트 스타일
 * @property {boolean} [essential] - 필수 여부
 * @property {Array<{text: string; value: any; rightImage?: string; rightImageStyle?: React.CSSProperties; rightImageOnclick?: () => void}>} [options] - 옵션 목록
 * @property {React.CSSProperties} [labelStyle] - 라벨 스타일
 * @property {React.CSSProperties} [labelInputContainerStyle] - 라벨 입력 컨테이너 스타일
 * @property {React.CSSProperties} [labelInputWrapStyle] - 라벨 입력 래퍼 스타일
 * @property {React.CSSProperties} [radioListStyle] - 라디오 리스트 스타일
 * @property {boolean} [readOnly] - 읽기 전용 여부
 * @property {React.CSSProperties} [formattedMessageStyle] - 포맷된 메시지 스타일
 * @property {string} [additionalLabelText] - 추가 라벨 텍스트
 */
interface Props {
  labelText?: string;
  value: any;
  setValue?: any;
  isColumn?: boolean;
  errorText?: string;
  errorTextStyle?: React.CSSProperties;
  essential?: boolean;
  options?: {
    text: string;
    value: any;
    rightImage?: string;
    rightImageStyle?: React.CSSProperties;
    rightImageOnclick?: () => void;
  }[];
  labelStyle?: React.CSSProperties;
  labelInputContainerStyle?: React.CSSProperties;
  labelInputWrapStyle?: React.CSSProperties;
  radioListStyle?: React.CSSProperties;
  readOnly?: boolean;
  formattedMessageStyle?: React.CSSProperties;
  additionalLabelText?: string;
}

/**
 * 라벨 라디오 컴포넌트
 * 라벨과 함께 라디오 버튼을 표시하는 컴포넌트입니다.
 * @param {Props} props - 라벨 라디오 컴포넌트의 props
 * @returns {JSX.Element} 라벨 라디오 컴포넌트
 */
const LabelRadio = ({
  labelText,
  value,
  setValue,
  isColumn,
  errorText,
  errorTextStyle,
  essential,
  options,
  labelStyle,
  labelInputContainerStyle,
  labelInputWrapStyle,
  radioListStyle,
  readOnly,
  formattedMessageStyle,
  additionalLabelText,
}: Props) => {
  const label = labelText && (useFormatMsg(labelText) || labelText);

  return (
    <LabelContainer style={labelInputContainerStyle}>
      <LabelInputWrap $isColumn={isColumn} style={labelInputWrapStyle}>
        {labelText && (
          <Label htmlFor={label} $isColumn={isColumn} style={labelStyle}>
            {additionalLabelText && (
              <AdditionalLabelText>{additionalLabelText}</AdditionalLabelText>
            )}
            {label}
            {essential && <span>*</span>}
          </Label>
        )}

        <RadioList style={radioListStyle}>
          {readOnly ? (
            <DefaultText
              text={
                (options && options.find(item => item.value === value)?.text) ??
                value
              }
              formattedMessageStyle={formattedMessageStyle}
            />
          ) : (
            options &&
            options.map((item, index) => (
              <RadioButton
                key={index}
                value={item.value}
                checked={item.value === value}
                onChange={() => setValue(item.value)}
                containerStyle={{alignItems: 'center'}}>
                <>
                  {item.text ? (
                    <DefaultText
                      text={item.text}
                      formattedMessageStyle={formattedMessageStyle}
                      textStyle={{marginLeft: minmax(8)}}
                    />
                  ) : (
                    <></>
                  )}
                  {item.rightImage ? (
                    <DefaultImageView
                      source={item.rightImage}
                      style={{
                        width: minmax(30),
                        height: minmax(30),
                        marginLeft: '2px',
                        ...item.rightImageStyle,
                      }}
                      onClick={(e: any) => {
                        e.stopPropagation();
                        e.preventDefault();
                        item.rightImageOnclick && item.rightImageOnclick();
                      }}
                    />
                  ) : (
                    <></>
                  )}
                </>
              </RadioButton>
            ))
          )}
        </RadioList>
      </LabelInputWrap>
      {errorText && (
        <ErrorTextStyle style={errorTextStyle}>{errorText}</ErrorTextStyle>
      )}
    </LabelContainer>
  );
};

export default LabelRadio;

const LabelContainer = styled.div`
  width: 100%;
  margin: 0 auto;
`;

const LabelInputWrap = styled.div<{$isColumn?: boolean}>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  word-break: break-all;

  ${(props: any) =>
    props.$isColumn &&
    css`
      flex-direction: column;
      height: auto;
    `}
`;

const Label = styled.label<{$isColumn?: boolean}>`
  font-size: ${minmax(20)};
  font-weight: 400;
  width: 20%;
  height: 100%;
  display: flex;
  justify-content: start;
  align-items: center;
  ${(props: any) =>
    props.$isColumn &&
    css`
      width: 100%;
      margin-bottom: 12px;
    `}

  span {
    font-size: initial;
    color: ${colors.primary};
  }
`;

const RadioList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  column-gap: 2%;
`;

const ErrorTextStyle = styled.div`
  width: 100%;
  margin-top: 8px;
  text-align: center;
  font-size: ${minmax(16)};
  color: ${colors.primary};
`;

const AdditionalLabelText = styled.pre`
  font-size: ${minmax(20)};
  font-weight: 400;
  display: flex;
  justify-content: start;
  align-items: center;
  color: transparent;
  ${(props: any) =>
    props.$isColumn &&
    css`
      width: 100%;
      margin-bottom: 12px;
    `}
  span {
    font-size: initial;
    color: ${colors.primary};
  }
`;
